import { CircleAlert } from 'lucide-react'
import { useId } from 'react'

import type { ErrorView } from '@krp/client-runtime'
import { StatusMessage } from '@krp/ui-kit'
import { ICON_SIZE_SMALL } from '../../../ownIcons'

/** Код и номер запроса для администратора — рядом с текстом отказа. */
function errorMeta(error: ErrorView): string {
  return error.correlationId === null ? `Код: ${error.code}` : `Код: ${error.code} · запрос ${error.correlationId}`
}

/**
 * Отказ записи: текст службы дословно, код и номер запроса, кнопка копирования (её даёт `StatusMessage`). Не исчезает сам.
 * Ответ не дошёл (`unknown-outcome`) — не «ошибка»: запись могла пройти, поэтому сначала перечитать, а не повторять.
 */
export function CommandError({ title, error }: { title: string; error: ErrorView }) {
  if (error.kind === 'unknown-outcome')
    return (
      <StatusMessage
        kind="unavailable"
        title="Ответ не получен — записалось ли, неизвестно"
        text={`${error.message} Перечитайте данные и только потом повторяйте.`}
        meta={errorMeta(error)}
      />
    )
  return <StatusMessage kind="error" title={title} text={error.message} meta={errorMeta(error)} />
}

/** Причина, по которой форму пока не отправить: значок и текст, не только цвет. */
export function Problem({ text }: { text: string | null }) {
  if (text === null) return null
  return (
    <p className="own-problem own-muted" role="note">
      <CircleAlert size={ICON_SIZE_SMALL} aria-hidden="true" />
      {text}
    </p>
  )
}

/** Поле даты `yyyy-MM-dd` с подписью (в ui-kit поля даты нет). */
export function DateField({ label, value, onChange, disabled = false }: { label: string; value: string; onChange: (value: string) => void; disabled?: boolean }) {
  const id = useId()
  return (
    <div className="krp-field">
      <label className="krp-field__label" htmlFor={id}>
        {label}
      </label>
      <input id={id} type="date" value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)} />
    </div>
  )
}

/** Многострочное поле (штрихкоды по одному в строке). */
export function TextAreaField({ label, value, onChange, hint }: { label: string; value: string; onChange: (value: string) => void; hint?: string }) {
  const id = useId()
  const hintId = useId()
  return (
    <div className="krp-field own-form__wide">
      <label className="krp-field__label" htmlFor={id}>
        {label}
      </label>
      <textarea
        id={id}
        className="own-textarea"
        value={value}
        rows={3}
        aria-describedby={hint === undefined ? undefined : hintId}
        onChange={(event) => onChange(event.target.value)}
      />
      {hint !== undefined && (
        <p id={hintId} className="own-muted">
          {hint}
        </p>
      )}
    </div>
  )
}
